import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface TimeEntry {
  id: number;
  clockInTime: string;
  clockOutTime: string | null;
  date: string;
  durationMinutes: number | null;
  username?: string; // For super admin view
}

interface ActivityData {
  username: string;
  period: string;
  summary: {
    totalSessionTimeMs: number;
    totalSessionTimeFormatted: string;
    totalActiveTimeMs: number;
    totalActiveTimeFormatted: string;
    totalIdleTimeMs: number;
    totalIdleTimeFormatted: string;
    activityPercentage: number;
    totalDaysWorked: number;
    totalClockSessions: number;
    totalPageVisits: number;
    uniquePagesVisited: number;
    domainsClaimedCount: number;
    claimedDomains: string[];
  };
  pageBreakdown: Array<{
    page: string;
    totalTimeMs: number;
    totalTimeFormatted: string;
    totalActiveTimeMs: number;
    totalActiveTimeFormatted: string;
    totalIdleTimeMs: number;
    totalIdleTimeFormatted: string;
    visits: number;
    avgTimePerVisit: number;
    avgTimePerVisitFormatted: string;
    avgActiveTimePerVisit: number;
    avgActiveTimePerVisitFormatted: string;
    avgIdleTimePerVisit: number;
    avgIdleTimePerVisitFormatted: string;
    activityPercentage: number;
  }>;
  dailyBreakdown: Array<{
    date: string;
    sessionTime: number;
    sessionTimeFormatted: string;
    pageVisits: { [page: string]: number };
    uniquePages: number;
  }>;
  clockedInPeriods: Array<{
    start: string;
    end: string;
    date: string;
    duration: number;
    durationFormatted: string;
  }>;
}

interface AnalyticsResponse {
  period: string;
  username: string;
  aggregateStats: {
    totalManagers: number;
    totalSessionTime: number;
    totalSessionTimeFormatted: string;
    totalActiveTime: number;
    totalActiveTimeFormatted: string;
    totalIdleTime: number;
    totalIdleTimeFormatted: string;
    averageActivityPercentage: number;
    totalPageVisits: number;
    topPages: Array<{
      page: string;
      totalTimeMs: number;
      totalTimeFormatted: string;
      visits: number;
      avgTimePerVisit: number;
      avgTimePerVisitFormatted: string;
    }>;
    totalDaysWorked: number;
    totalClockSessions: number;
    totalDomainsClaimed: number;
  };
  managerData: { [username: string]: ActivityData };
}

const ManageTime: React.FC = () => {
  const [user] = useState(authService.getCurrentUser());
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('today');
  const [selectedManager, setSelectedManager] = useState<string>('all');
  const [managers, setManagers] = useState<Array<{ username: string; firstName: string; lastName: string }>>([]);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());

  const isSuperAdmin = user?.userType === 'superadmin';
  const isManager = user?.userType === 'manager';

  useEffect(() => {
    if (isSuperAdmin) {
      loadManagers();
    }
    loadData();
  }, [selectedDate, selectedPeriod, selectedManager]);

  // Update daily total in real-time for active sessions
  useEffect(() => {
    if (!isManager) return;

    const hasActiveSession = timeEntries.some(entry => !entry.clockOutTime);
    if (!hasActiveSession) return;

    const interval = setInterval(() => {
      // Force a re-render to update the daily total calculation
      setTimeEntries(prev => [...prev]);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [timeEntries, isManager]);

  const loadManagers = async () => {
    try {
      const response = await fetch('/api/managers');
      const result = await response.json();
      if (result.success) {
        setManagers(result.data.filter((m: any) => m.userType === 'manager'));
      }
    } catch (error) {
      console.error('Error loading managers:', error);
    }
  };

  const loadData = async () => {
    if (!user) return;
    
    setLoading(true);
    setError('');

    try {
      // Load time entries for managers
      if (isManager) {
        const url = `/api/managers/${user.username}/time-entries${selectedDate ? `?date=${selectedDate}` : ''}`;
        const response = await fetch(url);
        const result = await response.json();
        if (result.success) {
          setTimeEntries(result.data || []);
        }
      } else if (isSuperAdmin) {
        // Load all time entries for super admin
        const response = await fetch('/api/managers/time-entries/all');
        const result = await response.json();
        if (result.success) {
          setTimeEntries(result.data || []);
        }
      }

      // Load analytics data (super admin only)
      if (isSuperAdmin) {
        const period = selectedPeriod === 'today' ? selectedDate : selectedPeriod;
        const url = selectedManager === 'all' 
          ? `/api/managers/analytics/${period}`
          : `/api/managers/analytics/${period}?username=${selectedManager}`;
        
        const response = await fetch(url);
        const result = await response.json();
        
        if (result.success) {
          setAnalyticsData(result.data);
        } else {
          setError(result.error || 'Failed to load analytics data');
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate daily totals for managers
  const calculateDailyTotal = () => {
    if (!isManager || timeEntries.length === 0) return 0;
    
    return timeEntries.reduce((total, entry) => {
      if (entry.durationMinutes) {
        return total + entry.durationMinutes;
      }
      // If still clocked in, calculate current duration
      if (!entry.clockOutTime && entry.clockInTime) {
        const now = Date.now();
        const clockInTime = new Date(entry.clockInTime).getTime();
        const currentDuration = Math.round((now - clockInTime) / (1000 * 60));
        return total + currentDuration;
      }
      return total;
    }, 0);
  };

  const formatDailyTotal = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York',
      timeZoneName: 'short'
    });
  };

  const formatDuration = (minutes: number | null) => {
    if (!minutes) return 'In progress';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const toggleDomainsExpanded = (username: string) => {
    setExpandedDomains(prev => {
      const newSet = new Set(prev);
      if (newSet.has(username)) {
        newSet.delete(username);
      } else {
        newSet.add(username);
      }
      return newSet;
    });
  };

  if (!user) {
    return <div className="p-6">Please log in to view this page.</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Time Management</h1>
        
        {/* Manager Controls */}
        {isManager && (
          <div className="flex gap-4">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
        )}
        
        {/* Super Admin Controls */}
        {isSuperAdmin && (
          <div className="flex gap-4">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            >
              <option value="today">Today</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="all">All Time</option>
            </select>
            
            {selectedPeriod === 'today' && (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
            )}
            
            <select
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            >
              <option value="all">All Managers</option>
              {managers.map(manager => (
                <option key={manager.username} value={manager.username}>
                  {manager.firstName} {manager.lastName} ({manager.username})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex justify-center items-center py-8">
          <div className="text-lg text-gray-600 dark:text-gray-300">Loading data...</div>
        </div>
      )}

      {error && (
        <div className="bg-red-100 dark:bg-red-900/50 border border-red-400 dark:border-red-500 text-red-700 dark:text-red-200 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Manager View - Time Entries */}
      {isManager && !loading && (
        <div className="space-y-6">
          {/* Daily Total Summary */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              Daily Total for {new Date(selectedDate).toLocaleDateString('en-US', { 
                weekday: 'long',
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                timeZone: 'America/New_York'
              })}
            </h2>
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 p-6 rounded-lg">
              <div className="text-center">
                <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">Total Time Worked</div>
                <div className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                  {formatDailyTotal(calculateDailyTotal())}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  {timeEntries.length} session{timeEntries.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>

          {/* Time Entries Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Time Entries</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Date</th>
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Clock In</th>
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Clock Out</th>
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {timeEntries.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        No time entries found for this date
                      </td>
                    </tr>
                  ) : (
                    timeEntries.map((entry) => (
                      <tr key={entry.id} className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{entry.date}</td>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{formatDateTime(entry.clockInTime)}</td>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                          {entry.clockOutTime ? formatDateTime(entry.clockOutTime) : (
                            <span className="text-green-600 dark:text-green-400 font-medium">Still clocked in</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{formatDuration(entry.durationMinutes)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Super Admin View - Analytics Dashboard */}
      {isSuperAdmin && analyticsData && !loading && (
        <div className="space-y-6">
          {/* Aggregate Statistics */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              {selectedManager === 'all' ? 'All Managers' : `${selectedManager}`} - {selectedPeriod === 'today' ? selectedDate : selectedPeriod}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 p-4 rounded">
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Session Time</div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {analyticsData.aggregateStats.totalSessionTimeFormatted}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {analyticsData.aggregateStats.totalManagers} manager(s)
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 p-4 rounded">
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Active Time</div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {analyticsData.aggregateStats.totalActiveTimeFormatted}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Avg. {analyticsData.aggregateStats.averageActivityPercentage}% activity
                </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 p-4 rounded">
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Idle Time</div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {analyticsData.aggregateStats.totalIdleTimeFormatted}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Time inactive (30s+ periods)
                </div>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700 p-4 rounded">
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Page Visits</div>
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {analyticsData.aggregateStats.totalPageVisits?.toLocaleString() || 0}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {analyticsData.aggregateStats.totalClockSessions} clock sessions
                </div>
              </div>
            </div>

            {/* Domain Claims Statistics */}
            <div className="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-700 p-4 rounded mb-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Domain Claims</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Total Domains Claimed</div>
                  <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                    {analyticsData.aggregateStats.totalDomainsClaimed || 0}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Across all managers for {selectedPeriod === 'today' ? selectedDate : selectedPeriod}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Avg. Claims per Manager</div>
                  <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                    {analyticsData.aggregateStats.totalManagers > 0 
                      ? Math.round((analyticsData.aggregateStats.totalDomainsClaimed || 0) / analyticsData.aggregateStats.totalManagers * 10) / 10
                      : 0
                    }
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Average domains claimed per manager
                  </div>
                </div>
              </div>

              {/* Manager Claims Breakdown - Only show when viewing all managers */}
              {selectedManager === 'all' && Object.keys(analyticsData.managerData).length > 1 && (
                <div className="mt-6">
                  <h4 className="text-md font-semibold mb-3 text-gray-900 dark:text-white">Claims by Manager</h4>
                  <div className="space-y-3">
                    {Object.entries(analyticsData.managerData)
                      .filter(([_, data]) => data.summary.domainsClaimedCount > 0)
                      .sort((a, b) => b[1].summary.domainsClaimedCount - a[1].summary.domainsClaimedCount)
                      .map(([managerUsername, data]) => (
                      <div key={managerUsername} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="font-medium text-gray-900 dark:text-white">{managerUsername}</span>
                            <span className="inline-flex items-center rounded-full bg-purple-100 dark:bg-purple-900/50 px-2 py-0.5 text-xs font-medium text-purple-800 dark:text-purple-200">
                              {data.summary.domainsClaimedCount} domain{data.summary.domainsClaimedCount !== 1 ? 's' : ''}
                            </span>
                          </div>
                          {data.summary.claimedDomains && data.summary.claimedDomains.length > 0 && (
                            <button
                              onClick={() => toggleDomainsExpanded(`aggregate-${managerUsername}`)}
                              className="flex items-center text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                            >
                              {expandedDomains.has(`aggregate-${managerUsername}`) ? (
                                <>
                                  <ChevronDown className="h-3 w-3 mr-1" />
                                  Hide
                                </>
                              ) : (
                                <>
                                  <ChevronRight className="h-3 w-3 mr-1" />
                                  View
                                </>
                              )}
                            </button>
                          )}
                        </div>
                        {expandedDomains.has(`aggregate-${managerUsername}`) && data.summary.claimedDomains && (
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {data.summary.claimedDomains.map((domain, index) => (
                              <div
                                key={index}
                                className="text-xs bg-purple-50 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 px-2 py-1 rounded border border-purple-200 dark:border-purple-700"
                              >
                                {domain}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    {Object.values(analyticsData.managerData).every(data => data.summary.domainsClaimedCount === 0) && (
                      <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                        No domains claimed by any manager during this period
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Top Pages */}
            <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Top Pages</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Page</th>
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Total Time</th>
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Total Visits</th>
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Avg. per Visit</th>
                  </tr>
                </thead>
                <tbody>
                  {analyticsData.aggregateStats.topPages?.slice(0, 10).map((page, index) => (
                    <tr key={index} className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-2 font-medium text-gray-900 dark:text-gray-100">{page.page}</td>
                      <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{page.totalTimeFormatted}</td>
                      <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{page.visits}</td>
                      <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{page.avgTimePerVisitFormatted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Individual Manager Data */}
          {Object.entries(analyticsData.managerData).map(([username, data]) => (
            <div key={username} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                {username} - Activity Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-3 rounded">
                  <div className="text-sm text-gray-600 dark:text-gray-300">Session Time</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">{data.summary.totalSessionTimeFormatted}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-3 rounded">
                  <div className="text-sm text-gray-600 dark:text-gray-300">Active Time</div>
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">{data.summary.totalActiveTimeFormatted}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-3 rounded">
                  <div className="text-sm text-gray-600 dark:text-gray-300">Idle Time</div>
                  <div className="text-lg font-bold text-red-600 dark:text-red-400">{data.summary.totalIdleTimeFormatted}</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-3 rounded">
                  <div className="text-sm text-gray-600 dark:text-gray-300">Activity %</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">{data.summary.activityPercentage}%</div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-3 rounded">
                  <div className="text-sm text-gray-600 dark:text-gray-300">Domains Claimed</div>
                  <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                    {data.summary.domainsClaimedCount || 0}
                  </div>
                  {data.summary.claimedDomains && data.summary.claimedDomains.length > 0 && (
                    <div className="mt-2">
                      <button
                        onClick={() => toggleDomainsExpanded(username)}
                        className="flex items-center text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      >
                        {expandedDomains.has(username) ? (
                          <>
                            <ChevronDown className="h-3 w-3 mr-1" />
                            Hide domains
                          </>
                        ) : (
                          <>
                            <ChevronRight className="h-3 w-3 mr-1" />
                            View all domains
                          </>
                        )}
                      </button>
                      {expandedDomains.has(username) && (
                        <div className="mt-2 max-h-32 overflow-y-auto">
                          <div className="space-y-1">
                            {data.summary.claimedDomains.map((domain, index) => (
                              <div
                                key={index}
                                className="text-xs bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 px-2 py-1 rounded"
                              >
                                {domain}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {(!data.summary.claimedDomains || data.summary.claimedDomains.length === 0) && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      No domains claimed
                    </div>
                  )}
                </div>
              </div>

              {/* Page breakdown for this manager */}
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700">
                      <th className="px-3 py-2 text-left text-gray-900 dark:text-gray-100">Page</th>
                      <th className="px-3 py-2 text-left text-gray-900 dark:text-gray-100">Total Time</th>
                      <th className="px-3 py-2 text-left text-gray-900 dark:text-gray-100">Active Time</th>
                      <th className="px-3 py-2 text-left text-gray-900 dark:text-gray-100">Idle Time</th>
                      <th className="px-3 py-2 text-left text-gray-900 dark:text-gray-100">Activity %</th>
                      <th className="px-3 py-2 text-left text-gray-900 dark:text-gray-100">Visits</th>
                      <th className="px-3 py-2 text-left text-gray-900 dark:text-gray-100">Avg/Visit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pageBreakdown.slice(0, 5).map((page, index) => (
                      <tr key={index} className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{page.page}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{page.totalTimeFormatted}</td>
                        <td className="px-3 py-2 text-green-600 dark:text-green-400">{page.totalActiveTimeFormatted}</td>
                        <td className="px-3 py-2 text-red-600 dark:text-red-400">{page.totalIdleTimeFormatted}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1 py-0.5 rounded text-xs font-semibold ${
                            page.activityPercentage >= 80 ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200' :
                            page.activityPercentage >= 60 ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200' :
                            'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200'
                          }`}>
                            {page.activityPercentage}%
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{page.visits}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{page.avgTimePerVisitFormatted}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Show clocked-in periods */}
              {data.clockedInPeriods.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2 text-gray-900 dark:text-white">Clock Sessions ({data.clockedInPeriods.length})</h4>
                  <div className="space-y-1 text-sm">
                    {data.clockedInPeriods.slice(0, 3).map((period, index) => (
                      <div key={index} className="flex justify-between text-gray-700 dark:text-gray-300">
                        <span>{new Date(period.start).toLocaleString()} - {new Date(period.end).toLocaleString()}</span>
                        <span className="font-medium text-gray-900 dark:text-white">{period.durationFormatted}</span>
                      </div>
                    ))}
                    {data.clockedInPeriods.length > 3 && (
                      <div className="text-gray-500 dark:text-gray-400">... and {data.clockedInPeriods.length - 3} more sessions</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageTime; 